import { Box, CircularProgress, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useTranslation } from 'react-i18next'
import { MIN_CARD_HEIGHT } from '../cardHeight'

const defaultColorFor = percent => {
  if (percent >= 90) return 'success'
  if (percent >= 70) return 'primary'
  return 'warning'
}

const clamp = value => Math.min(100, Math.max(0, value))

/**
 * @param sort       'asc' puts the lowest first — triage order rather than alphabetical.
 * @param reference  draws a marker line across every bar, so the gap to it is readable
 *                   at a glance. A percentage alone says little without one.
 * @param alertBelow flags any item this many points under `reference`.
 */
export default function ProgressBreakdown({
  title,
  items = [],
  overall,
  overallLabel = 'overall',
  isReady = true,
  colorFor = defaultColorFor,
  emptyMessage = 'No data to report.',
  minHeight = MIN_CARD_HEIGHT,
  sort,
  reference,
  referenceLabel,
  alertBelow,
}) {
  const { t } = useTranslation()

  const ordered =
    sort === 'asc'
      ? [...items].sort((a, b) => a.percent - b.percent)
      : sort === 'desc'
        ? [...items].sort((a, b) => b.percent - a.percent)
        : items

  const isAlerting = item =>
    alertBelow != null && reference != null && reference - item.percent >= alertBelow
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {overall != null && (
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="h5" fontWeight={700} color={`${colorFor(overall)}.main`}>
              {overall}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {overallLabel}
            </Typography>
          </Stack>
        )}
      </Stack>

      {!isReady ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        // the list scrolls, not the card — the title and overall figure stay put
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
          {ordered.map((item, index) => {
            const alerting = isAlerting(item)
            return (
              <Box key={item.label ?? index}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                    {alerting && (
                      <Tooltip title={t('Well below {{label}}', { label: referenceLabel ?? t('average') })}>
                        <WarningAmberIcon fontSize="small" color="error" />
                      </Tooltip>
                    )}
                    <Typography variant="body2" fontWeight={alerting ? 700 : 500} noWrap>
                      {item.label}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.caption != null && `${item.caption} · `}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {item.percent}%
                    </Box>
                  </Typography>
                </Stack>
                <Box sx={{ position: 'relative' }}>
                  <LinearProgress
                    variant="determinate"
                    value={clamp(item.percent)}
                    color={colorFor(item.percent)}
                    sx={{ height: 8, borderRadius: 5, bgcolor: 'action.hover' }}
                  />
                  {reference != null && (
                    <Tooltip title={`${referenceLabel ?? t('average')} · ${Math.round(reference)}%`}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          bottom: -2,
                          left: `${clamp(reference)}%`,
                          width: 2,
                          bgcolor: 'text.primary',
                          opacity: 0.5,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )
          })}
        </Stack>
      )}
    </Paper>
  )
}
